/* ============================================================
   CUSTOM SHOPIFY SEARCH
   ============================================================

   FEATURES
   ------------------------------------------------------------
   1. 90% product title matching
   2. Singular / plural matching
   3. Typo / fuzzy matching
   4. SKU matching
   5. Variant SKU matching
   6. SKU fallback search
   7. 12 products per page
   8. Custom pagination
   9. Correct result count
   10. Breadcrumb result count
   11. Predictive / AJAX search filtering
   12. Gift Card exclusion

   ============================================================ */


/* ============================================================
   GLOBAL SETTINGS
   ============================================================ */

const CUSTOM_SEARCH_CONFIG = {

  /* Minimum title/SKU score */
  minimumScore: 90,

  /* Products shown on customer-facing page */
  productsPerPage: 50,

  /* Shopify server-side products requested */
  serverProductsPerPage: 150,

  /* Maximum pages used for SKU fallback */
  maxSkuPages: 10,

  /* AJAX debounce */
  predictiveDebounce: 300

};


/* ============================================================
   GENERAL HELPERS
   ============================================================ */

function customSearchNormalizeText(text) {

  return String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

}


function customSearchNormalizeWord(word) {

  word =
    customSearchNormalizeText(word);

  if (!word) {
    return '';
  }


  /*
   * cities → city
   */

  if (
    word.endsWith('ies') &&
    word.length > 4
  ) {

    return (
      word.slice(0, -3) +
      'y'
    );

  }


  /*
   * boxes → box
   */

  if (
    word.endsWith('es') &&
    word.length > 4
  ) {

    return word.slice(0, -2);

  }


  /*
   * shirts → shirt
   */

  if (
    word.endsWith('s') &&
    word.length > 3
  ) {

    return word.slice(0, -1);

  }


  return word;

}


/* ============================================================
   LEVENSHTEIN DISTANCE
   ============================================================ */

function customSearchLevenshtein(a, b) {

  if (a === b) {
    return 0;
  }


  if (!a.length) {
    return b.length;
  }


  if (!b.length) {
    return a.length;
  }


  const matrix = [];


  for (
    let i = 0;
    i <= b.length;
    i++
  ) {

    matrix[i] = [i];

  }


  for (
    let j = 0;
    j <= a.length;
    j++
  ) {

    matrix[0][j] = j;

  }


  for (
    let i = 1;
    i <= b.length;
    i++
  ) {

    for (
      let j = 1;
      j <= a.length;
      j++
    ) {

      if (
        b.charAt(i - 1) ===
        a.charAt(j - 1)
      ) {

        matrix[i][j] =
          matrix[i - 1][j - 1];

      } else {

        matrix[i][j] =
          Math.min(

            matrix[i - 1][j - 1] + 1,

            matrix[i][j - 1] + 1,

            matrix[i - 1][j] + 1

          );

      }

    }

  }


  return matrix[b.length][a.length];

}


/* ============================================================
   WORD SIMILARITY
   ============================================================ */

function customSearchWordSimilarity(
  searchWord,
  productWord
) {

  searchWord =
    customSearchNormalizeWord(
      searchWord
    );


  productWord =
    customSearchNormalizeWord(
      productWord
    );


  if (
    !searchWord ||
    !productWord
  ) {

    return 0;

  }


  /*
   * Exact
   */

  if (
    searchWord ===
    productWord
  ) {

    return 100;

  }


  /*
   * Partial
   */

  if (
    productWord.includes(searchWord) ||
    searchWord.includes(productWord)
  ) {

    const shorter =
      Math.min(
        searchWord.length,
        productWord.length
      );


    const longer =
      Math.max(
        searchWord.length,
        productWord.length
      );


    return (
      shorter /
      longer
    ) * 100;

  }


  /*
   * Typo
   */

  const distance =
    customSearchLevenshtein(
      searchWord,
      productWord
    );


  const maxLength =
    Math.max(
      searchWord.length,
      productWord.length
    );


  if (!maxLength) {
    return 0;
  }


  return (
    (
      maxLength -
      distance
    ) /
    maxLength
  ) * 100;

}


/* ============================================================
   PRODUCT TEXT SCORE
   ============================================================ */

function customSearchCalculateScore(
  productText,
  searchTerm
) {

  const product =
    customSearchNormalizeText(
      productText
    );


  const search =
    customSearchNormalizeText(
      searchTerm
    );


  if (
    !product ||
    !search
  ) {

    return 0;

  }


  /*
   * Complete exact match
   */

  if (
    product === search
  ) {

    return 100;

  }


  const searchWords =
    search
      .split(' ')
      .filter(Boolean);


  const productWords =
    product
      .split(' ')
      .filter(Boolean);


  if (
    !searchWords.length ||
    !productWords.length
  ) {

    return 0;

  }


  let totalScore = 0;

  let matchedWords = 0;


  searchWords.forEach(
    (searchWord) => {

      let bestScore = 0;


      productWords.forEach(
        (productWord) => {

          const score =
            customSearchWordSimilarity(
              searchWord,
              productWord
            );


          if (
            score >
            bestScore
          ) {

            bestScore =
              score;

          }

        }
      );


      if (
        bestScore >=
        CUSTOM_SEARCH_CONFIG.minimumScore
      ) {

        matchedWords++;

        totalScore +=
          bestScore;

      }

    }
  );


  /*
   * Every search word must match
   */

  if (
    matchedWords !==
    searchWords.length
  ) {

    return 0;

  }


  return (
    totalScore /
    searchWords.length
  );

}


/* ============================================================
   SKU SCORE
   ============================================================ */

function customSearchCalculateSKUScore(
  skuString,
  searchTerm
) {

  if (!skuString) {
    return 0;
  }


  const skus =
    String(skuString)
      .split('||')
      .map(
        sku => sku.trim()
      )
      .filter(Boolean);


  const normalizedSearch =
    customSearchNormalizeText(
      searchTerm
    );


  const compactSearch =
    normalizedSearch.replace(
      /\s/g,
      ''
    );


  let bestScore = 0;


  skus.forEach(
    (sku) => {

      const normalizedSKU =
        customSearchNormalizeText(
          sku
        );


      const compactSKU =
        normalizedSKU.replace(
          /\s/g,
          ''
        );


      /*
       * Exact SKU
       */

      if (
        compactSKU ===
        compactSearch
      ) {

        bestScore = 100;

        return;

      }


      /*
       * SKU contains search
       *
       * This is intentionally 100%.
       *
       * Example:
       * Search ABC123
       * SKU ABC123-BLACK-M
       */

      if (
        compactSKU.includes(
          compactSearch
        )
      ) {

        bestScore =
          Math.max(
            bestScore,
            100
          );

        return;

      }


      /*
       * Fuzzy fallback
       */

      const score =
        customSearchCalculateScore(
          sku,
          searchTerm
        );


      if (
        score >
        bestScore
      ) {

        bestScore =
          score;

      }

    }
  );


  return bestScore;

}


/* ============================================================
   GET PRODUCT TITLE
   ============================================================ */

function customSearchGetProductTitle(
  element
) {

  /*
   * Attribute on element
   */

  const directTitle =
    element.getAttribute(
      'data-product-title'
    );


  if (directTitle) {

    return directTitle;

  }


  /*
   * Child attribute
   */

  const titleData =
    element.querySelector(
      '[data-product-title]'
    );


  if (
    titleData &&
    titleData.getAttribute(
      'data-product-title'
    )
  ) {

    return titleData.getAttribute(
      'data-product-title'
    );

  }


  /*
   * Common selectors
   */

  const titleElement =
    element.querySelector(
      [
        '.predictive-search__item-heading',
        '.predictive-search__item-title',
        '.card__heading',
        '.product-card__title',
        '.product-title',
        'h3',
        'h4'
      ].join(',')
    );


  if (
    titleElement &&
    titleElement.textContent.trim()
  ) {

    return titleElement.textContent.trim();

  }


  /*
   * Product link fallback
   */

  const link =
    element.querySelector(
      'a[href*="/products/"]'
    );


  if (link) {

    return link.textContent.trim();

  }


  return '';

}


/* ============================================================
   GET PRODUCT SKU
   ============================================================ */

function customSearchGetProductSKU(
  element
) {

  /*
   * Attribute directly on product element
   */

  const directSKU =
    element.getAttribute(
      'data-product-sku'
    );


  if (directSKU) {

    return directSKU;

  }


  /*
   * Child attribute
   */

  const skuElement =
    element.querySelector(
      '[data-product-sku]'
    );


  if (
    skuElement &&
    skuElement.getAttribute(
      'data-product-sku'
    )
  ) {

    return skuElement.getAttribute(
      'data-product-sku'
    );

  }


  /*
   * Other possible SKU selectors
   */

  const skuNode =
    element.querySelector(
      [
        '[data-sku]',
        '.product-sku',
        '.sku'
      ].join(',')
    );


  if (
    skuNode &&
    skuNode.textContent.trim()
  ) {

    return skuNode.textContent.trim();

  }


  return '';

}


/* ============================================================
   GET PRODUCT MATCH
   ============================================================ */

function customSearchGetMatch(
  element,
  searchTerm
) {

  const title =
    customSearchGetProductTitle(
      element
    );


  const sku =
    customSearchGetProductSKU(
      element
    );


  const titleScore =
    customSearchCalculateScore(
      title,
      searchTerm
    );


  const skuScore =
    customSearchCalculateSKUScore(
      sku,
      searchTerm
    );


  const bestScore =
    Math.max(
      titleScore,
      skuScore
    );


  return {

    element: element,

    title: title,

    sku: sku,

    titleScore: titleScore,

    skuScore: skuScore,

    score: bestScore,

    matchType:
      skuScore > titleScore
        ? 'sku'
        : 'title'

  };

}


/* ============================================================
   UPDATE BREADCRUMB COUNT
   ============================================================ */

function customSearchUpdateBreadcrumbCount(
  count
) {

  const breadcrumb =
    document.querySelector(
      '.wpbingo-breadcrumbs__title'
    );


  if (!breadcrumb) {
    return;
  }


  const text =
    breadcrumb.textContent.trim();


  /*
   * Replace:
   *
   * 1000 results
   * 1 result
   */

  const updated =
    text.replace(
      /\b\d+\s+results?\b/i,
      `${count} ${
        count === 1
          ? 'result'
          : 'results'
      }`
    );


  if (
    updated !== text
  ) {

    breadcrumb.textContent =
      updated;

  }

}


/* ============================================================
   UPDATE SEARCH RESULT COUNT
   ============================================================ */

function customSearchUpdateCount(
  count
) {

  /*
   * Common count selectors
   */

  const selectors = [

    '.wpbingo-breadcrumbs__title',

    '[data-search-result-count]',

    '.search-result-count',

    '.count-search'

  ];


  selectors.forEach(
    selector => {

      document
        .querySelectorAll(selector)
        .forEach(
          element => {

            /*
             * Breadcrumb handled separately
             */

            if (
              element.matches(
                '.wpbingo-breadcrumbs__title'
              )
            ) {

              return;

            }


            const text =
              element.textContent.trim();


            /*
             * Replace existing number
             */

            if (
              /\d+\s+results?/i.test(
                text
              )
            ) {

              element.textContent =
                text.replace(
                  /\d+\s+results?/i,
                  `${count} ${
                    count === 1
                      ? 'result'
                      : 'results'
                  }`
                );

            }

          }
        );

    }
  );


  customSearchUpdateBreadcrumbCount(
    count
  );

}


/* ============================================================
   NO RESULT MESSAGE
   ============================================================ */

function customSearchShowNoResults(
  searchTerm
) {

  const noMatch =
    document.querySelector(
      '#custom-search-no-match'
    );


  if (!noMatch) {
    return;
  }


  const query =
    noMatch.querySelector(
      '.custom-search-query'
    );


  if (query) {

    query.textContent =
      searchTerm;

  }


  noMatch.style.display =
    'block';

}


/* ============================================================
   HIDE NO RESULT MESSAGE
   ============================================================ */

function customSearchHideNoResults() {

  const noMatch =
    document.querySelector(
      '#custom-search-no-match'
    );


  if (!noMatch) {
    return;
  }


  noMatch.style.display =
    'none';

}


/* ============================================================
   GET MAIN SEARCH PRODUCTS
   ============================================================ */

function customSearchGetMainProducts() {

  return Array.from(
    document.querySelectorAll(
      '.custom-search-product'
    )
  );

}


/* ============================================================
   REMOVE NATIVE SHOPIFY PAGINATION
   ============================================================ */

function customSearchHideNativePagination() {

  const pagination =
    document.querySelector(
      '.js-collection-paginations'
    );


  if (pagination) {

    pagination.style.display =
      'none';

  }

}


/* ============================================================
   CREATE CUSTOM PAGINATION CONTAINER
   ============================================================ */

function customSearchGetPaginationContainer() {

  let container =
    document.querySelector(
      '#custom-search-pagination'
    );


  /*
   * Your current Liquid may not have this ID.
   *
   * Therefore create one automatically.
   */

  if (!container) {

    container =
      document.createElement(
        'div'
      );


    container.id =
      'custom-search-pagination';


    container.className =
      'js-collection-paginations page-collection-footer';


    const grid =
      document.querySelector(
        '.products__row.product-grid'
      );


    if (grid) {

      grid.parentNode.insertBefore(
        container,
        grid.nextSibling
      );

    }

  }


  return container;

}


/* ============================================================
   RENDER PAGINATION
   ============================================================ */

function customSearchRenderPagination(
  totalProducts,
  currentPage,
  renderPage
) {

  const container =
    customSearchGetPaginationContainer();


  if (!container) {
    return;
  }


  container.innerHTML =
    '';


  const totalPages =
    Math.ceil(
      totalProducts /
      CUSTOM_SEARCH_CONFIG.productsPerPage
    );


  /*
   * Only one page
   */

  if (
    totalPages <= 1
  ) {

    container.style.display =
      'none';

    return;

  }


  container.style.display =
    'block';


  const pagination =
    document.createElement(
      'div'
    );


  pagination.className =
    'pagination';


  /*
   * Previous
   */

  if (
    currentPage > 1
  ) {

    const previous =
      document.createElement(
        'button'
      );


    previous.type =
      'button';


    previous.className =
      'pagination__item pagination__previous';


    previous.innerHTML =
      '<i class="feather-chevron-left"></i>';


    previous.addEventListener(
      'click',
      () => {

        renderPage(
          currentPage - 1
        );

        window.scrollTo({
          top:
            document.querySelector(
              '#product-grid'
            )
              ? document.querySelector(
                  '#product-grid'
                ).offsetTop - 100
              : 0,

          behavior:
            'smooth'

        });

      }
    );


    pagination.appendChild(
      previous
    );

  }


  /*
   * Calculate visible page numbers
   */

  const pages = [];


  pages.push(1);


  if (
    currentPage > 4
  ) {

    pages.push(
      '...'
    );

  }


  const startPage =
    Math.max(
      2,
      currentPage - 1
    );


  const endPage =
    Math.min(
      totalPages - 1,
      currentPage + 1
    );


  for (
    let page = startPage;
    page <= endPage;
    page++
  ) {

    if (
      !pages.includes(page)
    ) {

      pages.push(page);

    }

  }


  if (
    currentPage <
    totalPages - 3
  ) {

    pages.push(
      '...'
    );

  }


  if (
    totalPages > 1
  ) {

    pages.push(
      totalPages
    );

  }


  /*
   * Page buttons
   */

  pages.forEach(
    page => {

      if (
        page === '...'
      ) {

        const dots =
          document.createElement(
            'span'
          );


        dots.className =
          'pagination__item pagination__dots';


        dots.textContent =
          '…';


        pagination.appendChild(
          dots
        );


        return;

      }


      const button =
        document.createElement(
          'button'
        );


      button.type =
        'button';


      button.className =
        'pagination__item';


      if (
        page === currentPage
      ) {

        button.classList.add(
          'active'
        );

        button.setAttribute(
          'aria-current',
          'page'
        );

      }


      button.textContent =
        page;


      button.addEventListener(
        'click',
        () => {

          renderPage(
            page
          );

          window.scrollTo({
            top:
              document.querySelector(
                '#product-grid'
              )
                ? document.querySelector(
                    '#product-grid'
                  ).offsetTop - 100
                : 0,

            behavior:
              'smooth'

          });

        }
      );


      pagination.appendChild(
        button
      );

    }
  );


  /*
   * Next
   */

  if (
    currentPage <
    totalPages
  ) {

    const next =
      document.createElement(
        'button'
      );


    next.type =
      'button';


    next.className =
      'pagination__item pagination__next';


    next.innerHTML =
      '<i class="feather-chevron-right"></i>';


    next.addEventListener(
      'click',
      () => {

        renderPage(
          currentPage + 1
        );


        window.scrollTo({
          top:
            document.querySelector(
              '#product-grid'
            )
              ? document.querySelector(
                  '#product-grid'
                ).offsetTop - 100
              : 0,

          behavior:
            'smooth'

        });

      }
    );


    pagination.appendChild(
      next
    );

  }


  container.appendChild(
    pagination
  );

}


/* ============================================================
   MAIN SEARCH FILTER
   ============================================================ */

function customSearchFilterMainResults() {

  const grid =
    document.querySelector(
      '.products__row.product-grid'
    );


  if (!grid) {
    return;
  }


  const searchGrid =
    document.querySelector(
      '.custom-search-product'
    );


  /*
   * Get query from URL
   */

  const params =
    new URLSearchParams(
      window.location.search
    );


  const searchTerm =
    params.get('q') ||
    '';


  if (!searchTerm.trim()) {
    return;
  }


  const products =
    customSearchGetMainProducts();


  /*
   * Hide Shopify pagination
   */

  customSearchHideNativePagination();


  /*
   * Calculate matches
   */

  const matches = [];


  products.forEach(
    product => {

      const result =
        customSearchGetMatch(
          product,
          searchTerm
        );


      console.log(
        '[Custom Search]',
        result.title,
        '| SKU:',
        result.sku,
        '| Title:',
        result.titleScore.toFixed(2) + '%',
        '| SKU:',
        result.skuScore.toFixed(2) + '%',
        '| Best:',
        result.score.toFixed(2) + '%'
      );


      if (
        result.score >=
        CUSTOM_SEARCH_CONFIG.minimumScore
      ) {

        matches.push(
          result
        );

      } else {

        product.style.display =
          'none';

      }

    }
  );


  /*
   * Sort highest score first
   */

  matches.sort(
    (a, b) =>
      b.score -
      a.score
  );


  /*
   * Remove duplicate product IDs
   */

  const uniqueMatches =
    [];


  const seenHandles =
    new Set();


  matches.forEach(
    match => {

      const handle =
        match.element.getAttribute(
          'data-product-handle'
        );


      if (
        handle &&
        seenHandles.has(handle)
      ) {

        return;

      }


      if (handle) {

        seenHandles.add(
          handle
        );

      }


      uniqueMatches.push(
        match
      );

    }
  );


  /*
   * Replace matches with unique matches
   */

  matches.length = 0;

  uniqueMatches.forEach(
    item => matches.push(item)
  );


  /*
   * Render page
   */

  function renderPage(
    page
  ) {

    const start =
      (
        page - 1
      ) *
      CUSTOM_SEARCH_CONFIG.productsPerPage;


    const end =
      start +
      CUSTOM_SEARCH_CONFIG.productsPerPage;


    const pageMatches =
      matches.slice(
        start,
        end
      );


    /*
     * Hide everything first
     */

    products.forEach(
      product => {

        product.style.display =
          'none';

      }
    );


    /*
     * Show current page
     */

    pageMatches.forEach(
      match => {

        match.element.style.display =
          '';

        grid.appendChild(
          match.element
        );

      }
    );


    /*
     * Pagination
     */

    customSearchRenderPagination(
      matches.length,
      page,
      renderPage
    );

  }


  /*
   * Count
   */

  customSearchUpdateCount(
    matches.length
  );


  /*
   * No matching products
   */

  if (
    matches.length === 0
  ) {

    customSearchShowNoResults(
      searchTerm
    );

  } else {

    customSearchHideNoResults();

    renderPage(1);

  }


  /*
   * Store globally.
   *
   * This allows SKU fallback to add products.
   */

  window.customSearchMainMatches =
    matches;

  window.customSearchMainGrid =
    grid;

  window.customSearchMainSearchTerm =
    searchTerm;

}


/* ============================================================
   SKU FALLBACK SEARCH
   ============================================================ */

async function customSearchSkuFallback() {

  const params =
    new URLSearchParams(
      window.location.search
    );


  const searchTerm =
    params.get('q') ||
    '';


  if (!searchTerm.trim()) {
    return;
  }


  /*
   * If we already have enough title results,
   * Shopify's normal search may be sufficient.
   *
   * But we still perform SKU fallback because
   * SKU products may be missing.
   */


  const encodedQuery =
    encodeURIComponent(
      `variants.sku:${searchTerm}`
    );


  /*
   * Get current search URL parameters
   */

  const currentUrl =
    new URL(
      window.location.href
    );


  const baseUrl =
    currentUrl.origin +
    currentUrl.pathname;


  let skuProducts = [];


  /*
   * Fetch a limited number of SKU search pages.
   *
   * This avoids the previous 80+ page problem.
   */

  for (
    let page = 1;
    page <=
      CUSTOM_SEARCH_CONFIG.maxSkuPages;
    page++
  ) {

    try {

      const url =
        `${baseUrl}?q=${encodedQuery}` +
        `&type=product` +
        `&page=${page}` +
        `&options%5Bprefix%5D=last`;


      console.log(
        '[Custom Search] SKU request:',
        url
      );


      const response =
        await fetch(
          url,
          {
            credentials:
              'same-origin'
          }
        );


      if (!response.ok) {

        console.warn(
          '[Custom Search] SKU search failed:',
          response.status
        );

        break;

      }


      const html =
        await response.text();


      const parser =
        new DOMParser();


      const documentHtml =
        parser.parseFromString(
          html,
          'text/html'
        );


      const products =
        Array.from(
          documentHtml.querySelectorAll(
            '.custom-search-product'
          )
        );


      if (!products.length) {

        break;

      }


      products.forEach(
        product => {

          const handle =
            product.getAttribute(
              'data-product-handle'
            );


          const sku =
            customSearchGetProductSKU(
              product
            );


          /*
           * Only accept products that
           * actually match SKU.
           */

          const skuScore =
            customSearchCalculateSKUScore(
              sku,
              searchTerm
            );


          if (
            skuScore >=
            CUSTOM_SEARCH_CONFIG.minimumScore
          ) {

            skuProducts.push({
              element: product,
              score: skuScore,
              handle: handle
            });

          }

        }
      );


      /*
       * If this page returned fewer than
       * the server limit, we reached the end.
       */

      const productCount =
        products.length;


      if (
        productCount <
        CUSTOM_SEARCH_CONFIG.serverProductsPerPage
      ) {

        break;

      }

    } catch (error) {

      console.error(
        '[Custom Search] SKU page error:',
        page,
        error
      );


      /*
       * Continue rather than completely
       * breaking the search.
       */

      continue;

    }

  }


  /*
   * Remove duplicate SKU products
   */

  const uniqueSKUProducts =
    [];


  const seenSKUHandles =
    new Set();


  skuProducts.forEach(
    product => {

      if (
        product.handle &&
        seenSKUHandles.has(
          product.handle
        )
      ) {

        return;

      }


      if (product.handle) {

        seenSKUHandles.add(
          product.handle
        );

      }


      uniqueSKUProducts.push(
        product
      );

    }
  );


  /*
   * Nothing found by SKU
   */

  if (
    !uniqueSKUProducts.length
  ) {

    return;

  }


  console.log(
    '[Custom Search] SKU products found:',
    uniqueSKUProducts.length
  );


  /*
   * Add SKU products to current grid
   */

  const grid =
    document.querySelector(
      '.products__row.product-grid'
    );


  if (!grid) {
    return;
  }


  /*
   * Existing product handles
   */

  const existingHandles =
    new Set(
      Array.from(
        grid.querySelectorAll(
          '.custom-search-product'
        )
      )
        .map(
          product =>
            product.getAttribute(
              'data-product-handle'
            )
        )
        .filter(Boolean)
    );


  /*
   * Add only new products
   */

  uniqueSKUProducts.forEach(
    skuProduct => {

      if (
        skuProduct.handle &&
        existingHandles.has(
          skuProduct.handle
        )
      ) {

        return;

      }


      const product =
        document.importNode(
          skuProduct.element,
          true
        );


      product.setAttribute(
        'data-search-score',
        skuProduct.score.toFixed(2)
      );


      product.setAttribute(
        'data-search-match',
        'sku'
      );


      product.style.display =
        'none';


      grid.appendChild(
        product
      );

    }
  );


  /*
   * Re-run complete main search
   *
   * This will now include the SKU products.
   */

  customSearchFilterMainResults();

}


/* ============================================================
   INITIALIZE MAIN SEARCH
   ============================================================ */

function customSearchInitializeMainSearch() {

  /*
   * Only execute on search page
   */

  if (
    !document.querySelector(
      '.template-search'
    )
  ) {

    return;

  }


  if (
    !document.querySelector(
      '.products__row.product-grid'
    )
  ) {

    return;

  }


  /*
   * Hide native pagination immediately
   */

  customSearchHideNativePagination();


  /*
   * Initial title/product filtering
   */

  customSearchFilterMainResults();


  /*
   * SKU fallback
   */

  customSearchSkuFallback();

}


/* ============================================================
   PREDICTIVE SEARCH
   ============================================================ */

class CustomPredictiveSearch {

  constructor(element) {

    this.element =
      element;

    this.input =
      element.querySelector(
        'input.field__input'
      );

    this.results =
      element.querySelector(
        '[data-predictive-search]'
      );

    this.articleResults =
      element.querySelector(
        '[data-predictive-search-article]'
      );


    this.setup();

  }


  /* ==========================================================
     SETUP
  ========================================================== */

  setup() {

    if (!this.input) {
      return;
    }


    this.input.addEventListener(
      'input',
      debounce(
        () => {

          this.search(
            this.input.value.trim()
          );

        },
        CUSTOM_SEARCH_CONFIG.predictiveDebounce
      )
    );

  }


  /* ==========================================================
     GET TITLE
  ========================================================== */

  getTitle(element) {

    const directTitle =
      element.getAttribute(
        'data-product-title'
      );


    if (directTitle) {

      return directTitle;

    }


    const title =
      element.querySelector(
        [
          '.predictive-search__item-heading',
          '.predictive-search__item-title',
          '.card__heading',
          '.product-card__title',
          '.product-title',
          'h3',
          'h4'
        ].join(',')
      );


    return title
      ? title.textContent.trim()
      : '';

  }


  /* ==========================================================
     GET SKU
  ========================================================== */

  getSKU(element) {

    const directSKU =
      element.getAttribute(
        'data-product-sku'
      );


    if (directSKU) {

      return directSKU;

    }


    const sku =
      element.querySelector(
        '[data-product-sku], [data-sku], .product-sku, .sku'
      );


    return sku
      ? (
          sku.getAttribute(
            'data-product-sku'
          ) ||
          sku.textContent.trim()
        )
      : '';

  }


  /* ==========================================================
     FILTER PRODUCTS
  ========================================================== */

  filterProducts(
    markup,
    searchTerm
  ) {

    const wrapper =
      document.createElement(
        'div'
      );


    wrapper.innerHTML =
      markup;


    /*
     * Find only product items.
     */

    let products =
      Array.from(
        wrapper.querySelectorAll(
          '#predictive-search-results-list > li'
        )
      );


    /*
     * Fallback
     */

    if (!products.length) {

      products =
        Array.from(
          wrapper.querySelectorAll(
            'li'
          )
        );

    }


    const matches = [];


    products.forEach(
      product => {

        /*
         * Don't treat no-results
         * elements as products.
         */

        if (
          product.classList.contains(
            'no_results'
          )
        ) {

          return;

        }


        const title =
          this.getTitle(
            product
          );


        const sku =
          this.getSKU(
            product
          );


        const titleScore =
          customSearchCalculateScore(
            title,
            searchTerm
          );


        const skuScore =
          customSearchCalculateSKUScore(
            sku,
            searchTerm
          );


        const score =
          Math.max(
            titleScore,
            skuScore
          );


        console.log(
          '[AJAX Search]',
          {
            title: title,
            sku: sku,
            titleScore:
              titleScore.toFixed(2) + '%',
            skuScore:
              skuScore.toFixed(2) + '%',
            bestScore:
              score.toFixed(2) + '%'
          }
        );


        if (
          score >=
          CUSTOM_SEARCH_CONFIG.minimumScore
        ) {

          product.setAttribute(
            'data-search-score',
            score.toFixed(2)
          );


          product.setAttribute(
            'data-search-match',
            skuScore > titleScore
              ? 'sku'
              : 'title'
          );


          matches.push(
            product
          );

        }

      }
    );


    /*
     * Remove original product results
     */

    products.forEach(
      product => {

        product.remove();

      }
    );


    /*
     * Find list
     */

    const list =
      wrapper.querySelector(
        '#predictive-search-results-list'
      ) ||
      wrapper.querySelector(
        'ul'
      );


    /*
     * Add matches
     */

    if (list) {

      matches.forEach(
        product => {

          list.appendChild(
            product
          );

        }
      );

    } else {

      matches.forEach(
        product => {

          wrapper.appendChild(
            product
          );

        }
      );

    }


    /*
     * Update count
     */

    const count =
      wrapper.querySelector(
        [
          '[data-results-count]',
          '.predictive-search__results-count',
          '.results-count',
          '.count-search'
        ].join(',')
      );


    if (count) {

      const number =
        matches.length;


      const text =
        count.textContent.trim();


      if (
        /\d+\s+results?/i.test(
          text
        )
      ) {

        count.textContent =
          text.replace(
            /\d+\s+results?/i,
            `${number} ${
              number === 1
                ? 'result'
                : 'results'
            }`
          );

      }

    }


    /*
     * No results
     */

    if (
      matches.length === 0
    ) {

      /*
       * Remove View All
       */

      const viewAll =
        wrapper.querySelector(
          '[data-view-all-product]'
        );


      if (viewAll) {

        const parent =
          viewAll.closest(
            'li, div'
          );


        if (parent) {

          parent.remove();

        } else {

          viewAll.remove();

        }

      }


      /*
       * Add custom message
       */

      const noResults =
        document.createElement(
          'li'
        );


      noResults.className =
        'custom-predictive-no-results';


      noResults.innerHTML = `
        <div
          style="
            padding:20px;
            text-align:center;
          "
        >
          No matching products found
        </div>
      `;


      if (list) {

        list.appendChild(
          noResults
        );

      } else {

        wrapper.appendChild(
          noResults
        );

      }

    }


    return wrapper.innerHTML;

  }


  /* ==========================================================
     SEARCH
     ========================================================== */

  search(searchTerm) {

    if (!searchTerm) {
      return;
    }


    const queryKey =
      searchTerm
        .replace(/\s+/g, '-')
        .toLowerCase();


    /*
     * PRODUCT SEARCH
     */

    /* ==========================================================
   PRODUCT SEARCH
========================================================== */

fetch(
  `${routes.predictive_search_url}` +
  `?q=${encodeURIComponent(searchTerm)}` +
  `&${encodeURIComponent('resources[type]')}=product` +
  `&${encodeURIComponent('resources[limit]')}=10` +
  `&${encodeURIComponent('resources[limit_scope]')}=each` +
  `&${encodeURIComponent('resources[options][unavailable_products]')}=show` +
  `&${encodeURIComponent('resources[options][fields]')}=title,product_type,variants.title,variants.sku` +
  `&section_id=predictive-search`
)

  .then(
    response => {

      if (!response.ok) {

        throw new Error(
          response.status
        );

      }

      return response.text();

    }
  )

  .then(
    text => {

      const parsed =
        new DOMParser()
          .parseFromString(
            text,
            'text/html'
          );

      const container =
        parsed.querySelector(
          '#predictive-search-product'
        );

      if (!container) {

        this.render(
          '',
          queryKey
        );

        return;

      }

      const filtered =
        this.filterProducts(
          container.innerHTML,
          searchTerm
        );

      this.render(
        filtered,
        queryKey
      );

    }
  )

  .catch(
    error => {

      console.error(
        '[AJAX Search]',
        error
      );

    }
  );


    /*
     * ARTICLE SEARCH
     */

    fetch(
      `${routes.predictive_search_url}` +
      `?q=${encodeURIComponent(searchTerm)}` +
      `&${encodeURIComponent('resources[type]')}=article` +
      `&section_id=predictive-search`
    )

      .then(
        response => {

          if (!response.ok) {

            throw new Error(
              response.status
            );

          }


          return response.text();

        }
      )

      .then(
        text => {

          const parsed =
            new DOMParser()
              .parseFromString(
                text,
                'text/html'
              );


          const articleContainer =
            parsed.querySelector(
              '#predictive-search-blog'
            );


          if (!articleContainer) {
            return;
          }


          if (
            this.articleResults
          ) {

            this.articleResults.innerHTML =
              articleContainer.innerHTML;

          }

        }
      )

      .catch(
        error => {

          console.error(
            '[AJAX Search Articles]',
            error
          );

        }
      );

  }


  /* ==========================================================
     RENDER
     ========================================================== */

  render(
    markup,
    queryKey
  ) {

    if (!this.results) {
      return;
    }


    this.results.innerHTML =
      markup;


    this.element.setAttribute(
      'results',
      true
    );


    /*
     * View all link
     */

    const viewAll =
      this.element.querySelector(
        '[data-predictive-search] [data-view-all-product]'
      );


    if (viewAll) {

  viewAll.setAttribute(
    'href',
    `/search?q=${encodeURIComponent(queryKey)}&type=product&options[unavailable_products]=show`
  );

}


    /*
     * Open
     */

    this.element.setAttribute(
      'open',
      true
    );


    this.input.setAttribute(
      'aria-expanded',
      true
    );

  }

}


/* ============================================================
   INITIALIZE PREDICTIVE SEARCH
   ============================================================ */

function customSearchInitializePredictiveSearch() {

  const predictiveSearchElements =
    document.querySelectorAll(
      'predictive-search'
    );


  predictiveSearchElements.forEach(
    element => {

      /*
       * Avoid duplicate initialization
       */

      if (
        element.dataset
          .customSearchInitialized ===
        'true'
      ) {

        return;

      }


      element.dataset
        .customSearchInitialized =
        'true';


      new CustomPredictiveSearch(
        element
      );

    }
  );

}


/* ============================================================
   INITIALIZE EVERYTHING
   ============================================================ */

function customSearchInitialize() {

  /*
   * Main search
   */

  customSearchInitializeMainSearch();


  /*
   * Predictive search
   */

  customSearchInitializePredictiveSearch();

}


/* ============================================================
   DOCUMENT READY
   ============================================================ */

if (
  document.readyState ===
  'loading'
) {

  document.addEventListener(
    'DOMContentLoaded',
    customSearchInitialize
  );

} else {

  customSearchInitialize();

}


/* ============================================================
   SHOPIFY SECTION RELOAD SUPPORT
   ============================================================ */

document.addEventListener(
  'shopify:section:load',
  () => {

    setTimeout(
      () => {

        customSearchInitialize();

      },
      100
    );

  }
);


/* ============================================================
   END
   ============================================================ */